import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import ArticleContent from '@/pages/detail/components/ArticleContent';
import ShareBar from '@/pages/detail/components/ShareBar';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const article = newsList.find((a) => a.id === id);

  usePageMeta({
    title: article ? `${article.title} - 原神情报站` : '内容未找到 - 原神情报站',
    description: article ? article.summary : '没有找到对应的资讯或攻略内容。',
    keywords: article ? article.tags.join(',') : '原神',
  });

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  if (!article) {
    return (
      <div className="container-page py-20">
        <EmptyState
          icon="ri-file-unknow-line"
          title="内容不存在或已被移除"
          description="你访问的资讯或攻略可能已经下线，返回首页继续浏览更多原神内容吧。"
          actionLabel="返回首页"
          onAction={() => navigate('/')}
        />
      </div>
    );
  }

  const related = [
    ...newsList.filter((a) => a.id !== article.id && a.category === article.category),
    ...newsList.filter((a) => a.id !== article.id && a.category !== article.category),
  ].slice(0, 3);

  return (
    <article>
      <section className="relative w-full">
        <div className="relative h-[280px] w-full overflow-hidden md:h-[400px]">
          <img
            src={article.cover}
            alt={article.title}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/30"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="container-page w-full pb-7 md:pb-10">
              <div className="max-w-3xl">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-white">
                  <i className={article.type === 'guide' ? 'ri-book-2-line' : 'ri-newspaper-line'}></i>
                  {article.category}
                </span>
                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-white md:text-4xl">
                  {article.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/80 md:text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-user-3-line"></i>
                    {article.author}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container-page py-6 md:py-10">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.type === 'guide' ? '角色攻略' : '资讯分类', to: article.type === 'guide' ? '/characters' : '/news' },
              { label: article.category },
            ]}
          />
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-arrow-left-line"></i>
            返回上一级
          </button>
        </div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ArticleContent sections={article.content} />

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-50 px-3 py-1.5 text-xs font-medium text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <ShareBar title={article.title} />
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-[88px] flex flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
                    <i className="ri-team-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                    <p className="text-xs text-foreground-500">原神情报站 · 内容团队</p>
                  </div>
                </div>
                <p className="mt-3 text-xs leading-6 text-foreground-500">
                  持续为旅行者整理版本动态、角色解读与玩法技巧，内容仅供学习交流参考。
                </p>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="mb-4 flex items-center gap-2">
                  <i className="ri-links-line text-primary-600"></i>
                  <h3 className="font-heading text-base font-bold text-foreground-950">相关推荐</h3>
                </div>
                <div className="flex flex-col gap-3">
                  {related.map((item) => (
                    <ArticleCard key={item.id} article={item} variant="horizontal" />
                  ))}
                </div>
              </div>

              <Link
                to={article.type === 'guide' ? '/characters' : '/news'}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                浏览更多{article.type === 'guide' ? '角色攻略' : '资讯'}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}